import { Dialog, Switch, Tab, Transition } from '@headlessui/react';
import React, {
	forwardRef,
	Fragment,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useDropzone } from 'react-dropzone';
import ReactQuill from 'react-quill';
import DatePicker from 'react-datepicker';
import { parseISO } from 'date-fns';
import {
	Bars2Icon,
	Bars4Icon,
	CalendarDaysIcon,
	ChatBubbleBottomCenterTextIcon,
	CloudIcon,
	PaperClipIcon,
	PencilIcon,
	XCircleIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import Compressor from 'compressorjs';

import { Field, FormRow, Label } from '../Form/tailwindForm';
import { EditableTextarea, Input, ReactSelect, Select } from '../Form';

// TODO: Update AttributesModal to use AssetSelectorBeta if still in use
// import SelectAsset from '../SelectAsset';
import SelectContacts from '../SelectContacts';

import { SelectCreatable } from '../SelectCreatable_old';
import Button from '../Button';
import Modal from '../Shell/Modal';

import { TAG_COLOR_OPTIONS } from '../helpers/constants';
import { classNames } from '../helpers/functions';

import useRequired from '../../hooks/useRequired';

import 'react-quill/dist/quill.snow.css';
import 'react-datepicker/dist/react-datepicker.css';

export const TemplateCreateable = React.forwardRef(
	(
		{
			name,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			options = [],
			placeholder,
			description,
			required,
			optional,
			disabled,
			isMulti,
			condensed,
			skip = [],
		},
		ref,
	) => {
		const newValue = value ? { label: value, value, __isNew__: true } : '';
		return (
			<FormRow key={key} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				{description && (
					<div className="text-sm text-gray-500 text-xs">{description}</div>
				)}
				<Field condensed={condensed}>
					{isEditing ? (
						<SelectCreatable
							options={
								options.map((option) =>
									skip?.includes(option?.value)
										? { ...option, isDisabled: true }
										: option,
								) || []
							}
							isClearable={true}
							isSearchable={true}
							placeholder={placeholder || 'Select or create...'}
							onChange={(option) => {
								onChange(dataIndex, option?.value, option);
							}}
							value={
								isMulti
									? value
									: options.find((o) => o.value === value) || newValue
							}
							isDisabled={disabled}
							isMulti={isMulti}
						/>
					) : (
						{ value }
					)}
				</Field>
			</FormRow>
		);
	},
);

export const AttributeRow = ({
	name,
	key,
	className,
	required,
	optional,
	description,
	span = 'sm:col-span-2',
	children,
	condensed,
	labelClassName,
}) => (
	<FormRow key={key} className={className} span={span}>
		<Label
			description={(required && '(Required)') || (optional && '(Optional)')}
			className={labelClassName}
		>
			{name}
		</Label>
		{description && (
			<div className="text-sm text-gray-500 text-xs">{description}</div>
		)}
		<Field condensed={condensed}>{children}</Field>
	</FormRow>
);

export const TemplateReactSelect = React.forwardRef(
	(
		{
			name,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			options = [],
			placeholder,
			description,
			required,
			optional,
			disabled,
			condensed,
			skip = [],
			skipOtherField,
			CustomOption,
			filterOption,
			formatOptionLabel,
			defaultValue,
			onMenuOpen,
			blurInputOnSelect,
		},
		ref,
	) => {
		return (
			<FormRow key={key} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				{description && (
					<div className="text-sm text-gray-500 text-xs">{description}</div>
				)}
				<Field condensed={condensed}>
					{isEditing ? (
						<>
							<ReactSelect
								onChange={(ticket) => {
									onChange(dataIndex, ticket);
								}}
								value={value}
								placeholder={placeholder || 'Select...'}
								options={
									Array.isArray(options)
										? options?.length
											? options.map((option) =>
													skip?.includes(option?.value)
														? { ...option, disabled: true }
														: option,
												)
											: []
										: options
								}
								CustomOption={CustomOption}
								filterOption={filterOption}
								disabled={disabled}
								formatOptionLabel={formatOptionLabel}
								defaultValue={defaultValue}
								onMenuOpen={onMenuOpen}
								blurInputOnSelect={blurInputOnSelect}
							/>
						</>
					) : (
						{ value }
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateDropdown = React.forwardRef(
	(
		{
			name,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			options = [],
			placeholder,
			description,
			required,
			optional,
			disabled,
			condensed,
			skip = [],
			skipOtherField,
			border,
		},
		ref,
	) => {
		return (
			<FormRow key={key} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				{description && (
					<div className="text-sm text-gray-500 text-xs">{description}</div>
				)}
				<Field condensed={condensed}>
					{isEditing ? (
						<>
							<Select
								onChange={({ label, value, ...additional }) =>
									onChange(dataIndex, value, { label, value, ...additional })
								}
								value={options.find((o) => o.value === value)}
								placeholder={placeholder || 'Select...'}
								options={options || []}
								disabled={disabled}
								className="placeholder:text-xs"
								border
							/>
							{!skipOtherField && value === 'other' && (
								<Field condensed={condensed}>
									<TemplateField
										placeholder="Please describe"
										dataIndex={'other'}
										onChange={(changes, val) => onChange(dataIndex, value, val)}
										value={value?.other}
										autoFocus
									/>
								</Field>
							)}
						</>
					) : (
						{ value }
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateDate = React.forwardRef(
	(
		{
			name,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			required,
			optional,
			placeholder,
			minDate,
			maxDate,
			condensed,
			disabled,
			valueClassName,
			onFocus,
			onBlur,
		},
		ref,
	) => {
		const formatDate = (date) => {
			if (!date) {
				return null;
			}
			if (typeof date === 'string') {
				return parseISO(value);
			}
			if (typeof date === 'object') {
				return value;
			}
		};

		return (
			<FormRow key={key} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				<Field condensed={condensed}>
					{isEditing ? (
						<>
							<DatePicker
								className="placeholder:text-xs block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
								wrapperClassName="block w-full"
								placeholderText={placeholder || name || 'Select a date'}
								selected={formatDate(value)}
								onChange={(date) => {
									onChange(dataIndex, date);
								}}
								dateFormat="MM/dd/yyyy"
								minDate={minDate}
								maxDate={maxDate}
								onFocus={() => (typeof onFocus === 'function' ? onFocus() : {})}
								onBlur={() => (typeof onBlur === 'function' ? onBlur() : {})}
								autoFocus={autoFocus}
							/>
						</>
					) : (
						<div
							className={classNames('text-xs', valueClassName)}
							onClick={onFocus}
						>
							{value}
						</div>
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateField = React.forwardRef(
	(
		{
			name,
			placeholder,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			defaultValue,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			disabled,
			required,
			optional,
			type,
			maxLength,
			showMaxLength,
			condensed,
			fieldClassName,
			onBlur,
			onFocus,
			onKeyDown,
			onClick,
		},
		ref,
	) => {
		return (
			<FormRow key={key} className={className} span={span}>
				{name && (
					<Label
						description={
							(required && '(Required)') || (optional && '(Optional)')
						}
					>
						{name}
					</Label>
				)}
				<Field condensed={condensed} className={fieldClassName}>
					{isEditing ? (
						<Input
							type={type}
							ref={ref}
							placeholder={placeholder || name}
							onChange={(e) => {
								onChange(dataIndex, e.target.value);
							}}
							value={value}
							defaultValue={defaultValue}
							autoFocus={autoFocus}
							onEnter={onEnter}
							disabled={disabled}
							maxLength={maxLength}
							showMaxLength={showMaxLength}
							onBlurBeta={onBlur}
							onFocus={onFocus}
							onKeyDown={onKeyDown}
						/>
					) : typeof onClick === 'function' ? (
						<div onClick={() => onClick()}>{value}</div>
					) : (
						value
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateFieldDollar = React.forwardRef(
	(
		{
			name,
			placeholder,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			disabled,
			required,
			optional,
			condensed,
		},
		ref,
	) => {
		return (
			<FormRow key={key} className={className} span={span}>
				{name && (
					<Label
						description={
							(required && '(Required)') || (optional && '(Optional)')
						}
					>
						{name}
					</Label>
				)}
				<Field condensed={condensed}>
					{isEditing ? (
						<div>
							<div className="relative rounded-md shadow-sm">
								<Input
									ref={ref}
									placeholder={placeholder || name || '0.00'}
									onChange={(e) => {
										onChange(dataIndex, e.target.value);
									}}
									value={value}
									autoFocus={autoFocus}
									onEnter={onEnter}
									disabled={disabled}
									className="pl-6"
								/>
								<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
									<span className="text-gray-500 sm:text-sm">$</span>
								</div>
								<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
									<span
										className="text-gray-500 sm:text-sm"
										id="price-currency"
									>
										USD
									</span>
								</div>
							</div>
						</div>
					) : (
						value
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateContact = React.forwardRef(
	(
		{
			name,
			placeholder,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			disabled,
			required,
			optional,
			isClearable,
			condensed,
		},
		ref,
	) => {
		return (
			<FormRow key={key} className={className} span={span}>
				{name && (
					<Label
						description={
							(required && '(Required)') || (optional && '(Optional)')
						}
					>
						{name}
					</Label>
				)}
				<Field condensed={condensed}>
					{isEditing ? (
						<SelectContacts
							onChange={(value) => {
								onChange(dataIndex, value);
							}}
							value={value}
							placeholder={placeholder || 'Select contact...'}
							isClearable
						/>
					) : (
						{ value }
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateTeamMembers = React.forwardRef(
	(
		{
			name,
			placeholder,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			disabled,
			required,
			optional,
			isClearable,
			condensed,
			members,
			skip = [],
			CustomOption,
		},
		ref,
	) => {
		return (
			<FormRow key={key} className={className} span={span}>
				{name && (
					<Label
						description={
							(required && '(Required)') || (optional && '(Optional)')
						}
					>
						{name}
					</Label>
				)}
				<Field condensed={condensed}>
					{isEditing ? (
						<SelectContacts
							onChange={(value) => {
								onChange(dataIndex, value);
							}}
							value={value}
							placeholder={placeholder || 'Select contact...'}
							isClearable
							contacts={members}
							skip={skip}
							Option={CustomOption}
						/>
					) : (
						{ value }
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateBlank = React.forwardRef(
	(
		{
			name,
			placeholder,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			disabled,
			children,
			required,
			optional,
			condensed,
		},
		ref,
	) => {
		return (
			<FormRow key={key} className={className} span={span}>
				{name && (
					<Label
						description={
							(required && '(Required)') || (optional && '(Optional)')
						}
					>
						{name}
					</Label>
				)}
				<Field condensed={condensed}>
					{React.cloneElement(children, { onChange, value })}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateBoolean = ({
	name,
	description,
	className,
	span = 'sm:col-span-1',
	dataIndex,
	value,
	isEditing,
	onChange,
	condensed,
	defaultValue,
	disabled,
	required,
	optional,
}) => {
	return (
		<FormRow className={className} span={span}>
			{name && (
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
			)}
			<Field condensed={condensed}>
				<Switch.Group as="div" className="flex items-center justify-between">
					<Switch
						defaultChecked={defaultValue === 'true'}
						disabled={disabled}
						checked={value}
						onChange={(v) => onChange(dataIndex, v)}
						className={classNames(
							value ? 'bg-indigo-600' : 'bg-gray-200',
							'relative inline-flex h-6 w-11 mr-4 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
						)}
					>
						<span
							aria-hidden="true"
							className={classNames(
								value ? 'translate-x-5' : 'translate-x-0',
								'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
							)}
						/>
					</Switch>
					<span className="flex flex-grow flex-col">
						<Switch.Description
							as="span"
							className="text-xxs text-gray-500 text-xs"
						>
							{description}
						</Switch.Description>
					</span>
				</Switch.Group>
			</Field>
		</FormRow>
	);
};

export const TemplateCheckbox = ({
	name,
	description,
	className,
	span = 'sm:col-span-1',
	dataIndex,
	value,
	isEditing,
	onChange,
	condensed,
	options,
	defaultValue,
	labelClassName,
}) => {
	return (
		<FormRow className={className} span={span}>
			<Field condensed={condensed}>
				<fieldset>
					<div className="space-y-2">
						{options?.map((option) => {
							const key = option?.value?.replace(/\s+/g, '-').toLowerCase();
							return (
								<div className="relative flex items-start">
									<div className="flex h-6 items-center">
										<input
											id={key}
											aria-describedby="comments-description"
											name={key}
											type="checkbox"
											className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
											onChange={(e) => {
												onChange(dataIndex, {
													option,
													value: e?.target?.checked,
												});
											}}
										/>
									</div>
									<div className="ml-3 text-sm leading-6">
										<label
											htmlFor={key}
											className={classNames(
												'font-medium text-gray-900',
												labelClassName,
											)}
										>
											{option?.label}
										</label>
										{option?.description && (
											<span id="comments-description" className="text-gray-500">
												{option?.description}
											</span>
										)}
									</div>
								</div>
							);
						})}
					</div>
				</fieldset>
			</Field>
		</FormRow>
	);
};

export const UpdatedTemplateTextarea = React.forwardRef(
	(
		{
			name,
			className,
			placeholder,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			required,
			optional,
			autoFocus,
			condensed,
			key,
			editOnClick,
			onBlur,
		},
		ref,
	) => {
		const [editing, setEditing] = useState(isEditing);
		return (
			<FormRow key={key} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				<Field condensed={condensed}>
					{editing ? (
						<textarea
							className="h-auto whitespace-pre-wrap	block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 placeholder:text-xs text-xs shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-xs"
							placeholder={placeholder}
							onChange={(e) => onChange(dataIndex, e.target.value)}
							autoFocus={autoFocus}
							onBlur={(e) => {
								if (typeof onBlur === 'function') {
									onBlur(e.target.value);
									setEditing(isEditing);
								}
							}}
							onFocus={(e) => {
								e.currentTarget.setSelectionRange(
									e.currentTarget.value.length,
									e.currentTarget.value.length,
								);
							}}
						>
							{value}
						</textarea>
					) : (
						<div
							className="group flex"
							onClick={editOnClick ? () => setEditing(true) : () => {}}
						>
							{value || (
								<div className="text-gray-400 cursor-pointer">
									{placeholder}
								</div>
							)}
							<PencilIcon className="opacity-0 group-hover:opacity-100 w-4 h-4 text-gray-400 ml-1.5 cursor-pointer" />
						</div>
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateTextarea = React.forwardRef(
	(
		{
			name,
			className,
			placeholder,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			required,
			optional,
			autoFocus,
			condensed,
		},
		ref,
	) => {
		return (
			<FormRow key={dataIndex} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				<Field condensed={condensed}>
					{isEditing ? (
						<EditableTextarea
							placeholder={placeholder}
							isEditable
							isEditing
							onChange={(e) => onChange(dataIndex, e.target.value)}
							autoFocus={autoFocus}
						>
							{value}
						</EditableTextarea>
					) : (
						value
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateEditor = React.forwardRef(
	(
		{
			name,
			className,
			placeholder,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			required,
			optional,
			autoFocus,
			condensed,
			toolbar = true,
			quillClassName,
		},
		ref,
	) => {
		return (
			<FormRow key={dataIndex} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				<Field condensed={condensed}>
					{isEditing ? (
						<ReactQuill
							className={classNames('rounded-xl', quillClassName)}
							style={{ borderRadius: 12 }}
							modules={{
								toolbar,
							}}
							defaultValue={value ? JSON.parse(value) : ''}
							onChange={(e, delta, source, editor) => {
								onChange(dataIndex, JSON.stringify(editor.getContents()), {
									delta: JSON.stringify(editor.getContents()),
									content: e,
								});
							}}
							placeholder={placeholder}
						/>
					) : (
						<div
							className="ql-editor"
							dangerouslySetInnerHTML={{ __html: value }}
						/>
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateColor = React.forwardRef(
	(
		{
			name,
			placeholder,
			key,
			className,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			defaultValue,
			isEditing = true,
			onChange,
			autoFocus,
			onEnter,
			disabled,
			required,
			optional,
			type,
			maxLength,
			showMaxLength,
			condensed,
			fieldClassName,
			onBlur,
		},
		ref,
	) => {
		const [currentColor, setCurrentColor] = useState(defaultValue);
		//const [pickerVisible, setPickerVisible] = useState(false);

		useEffect(() => {
			if (value) {
				setCurrentColor(value);
			}
		}, [value]);

		return (
			<FormRow key={key} className={className} span={span}>
				{name && (
					<Label
						description={
							(required && '(Required)') || (optional && '(Optional)')
						}
					>
						{name}
					</Label>
				)}
				<Field condensed={condensed} className={fieldClassName}>
					{isEditing ? (
						<>
							<div className="grid grid-cols-8 mt-3">
								{TAG_COLOR_OPTIONS?.map((color) => (
									<div className="flex justify-center">
										<div
											className={classNames(
												`w-5 h-5 rounded ${color?.preview} ring-offset-2 cursor-pointer`,
												color?.key === currentColor ? 'ring-2' : 'ring-0',
											)}
											onClick={() => {
												setCurrentColor(color?.key);
												onChange(dataIndex, color?.key);
											}}
										/>
									</div>
								))}
							</div>
						</>
					) : (
						value
					)}
				</Field>
			</FormRow>
		);
	},
);

export const TemplateImage = React.forwardRef(
	(
		{
			name,
			className,
			placeholder,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			required,
			optional,
			autoFocus,
			condensed,
			maxImages,
			previewImage = true,
		},
		ref,
	) => {
		const [imagePreview, setImagePreview] = useState(false);
		const [isUploading, setIsUploading] = useState(false);
		const [fileList, setFileList] = useState([]);

		useEffect(() => {
			onChange(dataIndex, fileList);
		}, [fileList]);

		return (
			<FormRow key={dataIndex} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				<Field condensed={condensed}>
					{isEditing ? (
						<>
							<div className="mt-1">
								<div className="mt-1 sm:col-span-2 sm:mt-0">
									{maxImages === 1 && fileList?.length === 1 ? null : (
										<label
											htmlFor="file-upload"
											className="relative rounded-md bg-white font-medium text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:text-indigo-500"
										>
											<div className="flex max-w-lg justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6 cursor-pointer ">
												<div className="space-y-1 text-center">
													<svg
														className="mx-auto h-12 w-12 text-gray-400"
														stroke="currentColor"
														fill="none"
														viewBox="0 0 48 48"
														aria-hidden="true"
													>
														<path
															d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
															strokeWidth={2}
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
													<div className="text-sm text-gray-600">
														<span>Upload a photo</span>
														<input
															accept="image/jpeg, image/png"
															id="file-upload"
															name="file-upload"
															type="file"
															className="sr-only"
															onChange={(e) => {
																setIsUploading(true);

																const reader = new FileReader();
																const imageFile = e.target.files[0];

																new Compressor(imageFile, {
																	quality: 0.2,
																	success: (compressedResult) => {
																		reader.readAsDataURL(compressedResult);
																	},
																});

																reader.onloadend = (result) => {
																	setIsUploading(false);
																	setFileList([
																		...fileList,
																		{
																			name: imageFile.name,
																			original: imageFile,
																			size: result.total,
																			thumbUrl: reader.result,
																		},
																	]);
																};
															}}
														/>
													</div>
													<p className="text-xs text-gray-500">
														PNG, JPG, up to 10MB
													</p>
												</div>
											</div>
										</label>
									)}
								</div>

								{maxImages === 1 && fileList?.length === 1 ? (
									<div>
										<img
											class="h-auto max-w-full"
											src={fileList[0]?.thumbUrl}
											alt="image description"
										/>
										<div className="relative">
											<Button type="secondary" onClick={() => setFileList([])}>
												<span className="text-xxs">Remove Image</span>
											</Button>
										</div>
									</div>
								) : (
									<ul
										role="list"
										className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 xl:gap-x-8"
									>
										{fileList.map((file) => (
											<li key={file.thumbUrl} className="relative">
												<div
													onClick={() => setImagePreview(file.thumbUrl)}
													className="group aspect-h-7 aspect-w-10 block w-full overflow-hidden rounded-lg bg-gray-100 focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 focus-within:ring-offset-gray-100"
												>
													<img
														src={file.thumbUrl}
														alt=""
														className="pointer-events-none object-cover group-hover:opacity-75"
													/>
													<button
														type="button"
														className="absolute inset-0 focus:outline-none"
													>
														<span className="sr-only">
															View details for {file.name}
														</span>
													</button>
												</div>
												<p className="pointer-events-none mt-2 block truncate text-xs font-medium text-gray-900">
													{file.name}
												</p>
												<p className="pointer-events-none block text-xxs font-medium text-gray-500">{`${(
													file.size /
													(1024 * 1024)
												).toFixed(2)} MB`}</p>
											</li>
										))}
									</ul>
								)}

								{isUploading && (
									<div className="relative flex flex-col items-center justify-center">
										<div role="status">
											<svg
												aria-hidden="true"
												class="w-5 h-5 mr-2 text-gray-200 animate-spin dark:text-gray-200 fill-blue-600"
												viewBox="0 0 100 101"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
													fill="currentColor"
												/>
												<path
													d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
													fill="currentFill"
												/>
											</svg>
											<span class="sr-only">Loading...</span>
										</div>
									</div>
								)}
							</div>
						</>
					) : (
						value
					)}
				</Field>
				{previewImage && (
					<Modal
						title="Preview Image"
						open={!!imagePreview}
						onClose={() => setImagePreview(false)}
						size="xl"
					>
						<img
							class="h-auto max-w-full"
							src={imagePreview}
							alt="image description"
						/>{' '}
					</Modal>
				)}
			</FormRow>
		);
	},
);

export const TemplateFile = React.forwardRef(
	(
		{
			name,
			className,
			placeholder,
			span = 'sm:col-span-2',
			dataIndex,
			value,
			isEditing = true,
			onChange,
			required,
			optional,
			autoFocus,
			condensed,
			allowUpload = true,
			maxFiles = 1,
		},
		ref,
	) => {
		const { acceptedFiles, isDragActive, getRootProps, getInputProps } =
			useDropzone({
				accept: 'application/pdf',
				maxFiles,
			});

		useEffect(() => {
			if (acceptedFiles?.length) {
				onChange(dataIndex, acceptedFiles);
			}
		}, [acceptedFiles]);

		return (
			<FormRow key={dataIndex} className={className} span={span}>
				<Label
					description={(required && '(Required)') || (optional && '(Optional)')}
				>
					{name}
				</Label>
				<Field condensed={condensed}>
					{isEditing ? (
						<>
							<div className="mt-1">
								<div className="mt-1 sm:col-span-1 sm:mt-0">
									{allowUpload && !value?.length ? (
										<div
											className="w-full border-2 border-dashed rounded-md flex items-center justify-center py-12 text-center text-xs text-gray-700 cursor-pointer"
											{...getRootProps()}
											style={{ marginBottom: 15 }}
										>
											<input {...getInputProps()} />
											{isDragActive ? (
												<p>Place files here</p>
											) : (
												<div className="drop-container text-center">
													<CloudIcon className={'w-6 h-6 m-auto mb-2'} />
													<p>Upload a PDF</p>
												</div>
											)}
										</div>
									) : null}
									{value?.length ? (
										<>
											<ul
												role="list"
												className="divide-y divide-gray-200 rounded-md border border-gray-200"
											>
												{value?.map((attachment) => (
													<li
														key={attachment.name}
														className="flex items-center justify-between py-4 pl-3 pr-4 text-sm"
													>
														<div className="flex w-0 flex-1 items-center">
															{attachment?.loading ? (
																<div role="status">
																	<svg
																		aria-hidden="true"
																		class="w-5 h-5 mr-2 text-gray-200 animate-spin dark:text-gray-200 fill-blue-600"
																		viewBox="0 0 100 101"
																		fill="none"
																		xmlns="http://www.w3.org/2000/svg"
																	>
																		<path
																			d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
																			fill="currentColor"
																		/>
																		<path
																			d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
																			fill="currentFill"
																		/>
																	</svg>
																	<span class="sr-only">Loading...</span>
																</div>
															) : (
																<PaperClipIcon
																	className="h-4 w-4 flex-shrink-0 text-gray-400"
																	aria-hidden="true"
																/>
															)}

															<span className="ml-2 w-0 flex-1 truncate text-xs">
																{attachment?.path ||
																	attachment.name ||
																	'Untitled'}
															</span>
														</div>
														{!attachment?.loading && (
															<div className="ml-4 flex-shrink-0">
																<XMarkIcon
																	className="h-4 w-4 flex-shrink-0 text-gray-400"
																	onClick={() => onChange(dataIndex, null)}
																/>
															</div>
														)}
													</li>
												))}
											</ul>

											{allowUpload && maxFiles > 1 ? (
												<div {...getRootProps()}>
													<input {...getInputProps()} />
													<Button type="ghost">Add another file</Button>
												</div>
											) : null}
										</>
									) : null}
								</div>
							</div>
						</>
					) : (
						value
					)}
				</Field>
			</FormRow>
		);
	},
);

export const Attribute = ({
	name,
	dataIndex,
	description,
	disabled,
	onClick,
	Icon,
	onClose,
}) => (
	<div
		key={dataIndex}
		className={classNames(
			'group relative flex items-center space-x-3 rounded-lg border bg-white px-4 py-3 shadow-sm',
			onClick && !disabled
				? 'cursor-pointer border-gray-200 hover:border-gray-300'
				: 'border-gray-100',
		)}
		onClick={() =>
			typeof onClick === 'function' && !disabled ? onClick(dataIndex) : {}
		}
	>
		{typeof onClose === 'function' && (
			<div className="absolute -right-2 -top-2 opacity-0 group-hover:opacity-100 transition duration-200 ease-in-out cursor-pointer z-10">
				<XCircleIcon
					className="w-6 h-6 text-gray-400 cursor-pointer"
					onClick={() => onClose()}
				/>
			</div>
		)}

		{Icon && (
			<div className="flex-shrink-0">
				<Icon
					className={classNames(
						'w-5 h-5 ',
						disabled ? 'text-gray-200' : 'text-gray-400',
					)}
				/>
			</div>
		)}

		<div className="min-w-0 flex-1">
			<span className="absolute inset-0" aria-hidden="true" />
			<p
				className={classNames(
					'text-sm font-medium ',
					disabled ? 'text-gray-300' : 'text-gray-900',
				)}
			>
				{name}
			</p>
			{description && (
				<p
					className={classNames(
						'truncate text-xs',
						disabled ? 'text-gray-200' : 'text-gray-500',
					)}
				>
					{description}
				</p>
			)}
		</div>
	</div>
);

export const AttributesModal = ({ details, onClose, value: defaultValue }) => {
	const [value, setValue] = useState({});

	useEffect(() => {
		if (details) {
			setValue({});
		}
	}, [details]);

	const hasAllRequired = useRequired(
		value,
		details?.fields
			?.filter((field) => field.required)
			.map((field) => field.key),
	);

	return (
		<Transition.Root show={!!details} as={Fragment}>
			<Dialog as="div" className="relative z-10" onClose={onClose}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
				</Transition.Child>

				<div className="fixed inset-0 z-10 overflow-y-auto">
					<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<Dialog.Panel className="relative transform rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
								{details?.fields?.map((field) => {
									if (field.type === 'asset') {
										// TODO: Update to use AssetSelectorBeta if this code path is still in use
										return (
											<div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded">
												<p className="text-sm text-yellow-800">
													Asset selection in AttributesModal needs to be updated
													to use AssetSelectorBeta
												</p>
											</div>
										);
									}
									if (field.type === 'contact') {
										return (
											<SelectContacts
												onChange={(newvalue) => {
													details?.onSubmit({ value: newvalue });
												}}
												value={value[field.key]}
												skip={field.skip}
											/>
										);
									}
									if (field.type === 'contracts') {
										return (
											<TemplateDropdown
												onChange={(newvalue) => {
													details?.onSubmit({ value: newvalue });
												}}
												options
												value={value[field.key]}
												skip={field.skip}
											/>
										);
									}
									if (field.type === 'textfield') {
										return (
											<TemplateField
												name={field.label}
												dataIndex={field.key}
												onChange={(changes, val, other) => {
													setValue({
														...value,
														[field.key]: val,
														...(other ? { other } : {}),
													});
												}}
												autoFocus
												className="mb-6"
												value={field.value}
											/>
										);
									}
									if (field.type === 'textfield-dollar') {
										return (
											<TemplateFieldDollar
												name={field.label}
												placeholder={field.placeholder}
												dataIndex={field.key}
												onChange={(changes, val) => {
													setValue({ ...value, [field.key]: val });
												}}
												autoFocus
												className="mb-6"
											/>
										);
									}
									if (field.type === 'textarea') {
										return (
											<TemplateTextarea
												name={field.label}
												dataIndex={field.key}
												onChange={(changes, val) => {
													setValue({ ...value, [field.key]: val });
												}}
												value={value[field.key]}
												autoFocus
												className="mb-6"
											/>
										);
									}
									if (field.type === 'booleanfield') {
										return (
											<TemplateBoolean
												name={field.label}
												dataIndex={field.key}
												onChange={(changes, val) => {
													setValue({ ...value, [field.key]: val });
												}}
												value={value[field.key]}
												autoFocus
												className="mb-6"
												description={field.description}
											/>
										);
									}
									if (field.type === 'fielddropdown') {
										return (
											<>
												<TemplateDropdown
													name={field.label}
													description={field.description}
													options={field.options}
													dataIndex={field.key}
													onChange={(changes, val, other) => {
														setValue({
															...value,
															[field.key]: val,
															...(other ? { other } : {}),
														});
													}}
													value={value[field.key] || field.value}
													autoFocus
													className="mb-6"
													placeholder={field.placeholder}
													skip={field.skip}
												/>
											</>
										);
									}
									if (field.type === 'datefield') {
										return (
											<TemplateDate
												name={field.label}
												dataIndex={field.key}
												onChange={(changes, val) => {
													setValue({ ...value, [field.key]: val });
												}}
												autoFocus
												className="mb-4"
												value={value[field.key]}
											/>
										);
									}
									return null;
								})}
								<div className="mt-5 sm:mt-6 flex justify-end">
									<Button
										disabled={!hasAllRequired}
										onClick={() => details?.onSubmit(value)}
										className=""
									>
										{details?.submitLabel || 'Add'}
									</Button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition.Root>
	);
};

const AttributesSection = ({ name, description, attributes }) => (
	<>
		<div className="my-6">
			<div className="text-gray-900 text-sm font-bold mb-1">{name}</div>
			<div className="text-sm text-gray-400">{description}</div>
		</div>

		<div className="grid grid-cols-1 gap-4">
			{attributes?.map((attribute) => (
				<Attribute {...attribute} />
			))}
		</div>
	</>
);

export const AttributeFormSelect = ({ tabs = [] }) => {
	return (
		<Tab.Group>
			{tabs.length > 1 ? (
				<Tab.List className="flex space-x-1 rounded-xl bg-gray-500/20 p-0.5">
					{tabs.map((category) => (
						<Tab
							key={category.name}
							className={({ selected }) =>
								classNames(
									'w-full rounded-lg py-2.5 text-sm font-medium text-gray-700',
									selected
										? 'bg-white shadow'
										: 'text-gray-300 hover:bg-white/[0.12] hover:text-gray-500',
								)
							}
						>
							{category.name}
						</Tab>
					))}
				</Tab.List>
			) : null}

			<Tab.Panels className="mt-2">
				{tabs.map(({ sections, ...rest }, idx) => (
					<Tab.Panel
						key={idx}
						className={classNames('rounded-xl bg-white p-3 outline-0')}
					>
						{sections?.length ? (
							sections?.map((section) => <AttributesSection {...section} />)
						) : (
							<AttributesSection {...rest} />
						)}
					</Tab.Panel>
				))}
			</Tab.Panels>
		</Tab.Group>
	);
};

export const textFieldConfig = ({
	name = 'Text Field',
	dataIndex = 'textField',
	description = 'A simple text field',
	onOpenModal,
	defaultValues = { label: undefined, value: undefined },
	onSubmit,
	isEditing,
}) => {
	return {
		name,
		dataIndex,
		description,
		onClick: () => {
			onOpenModal({
				fields: [
					...(isEditing
						? [
								{
									label: defaultValues.label,
									type: 'textfield',
									key: 'value',
									value: defaultValues.value,
								},
							]
						: [
								{
									label: 'Name of field',
									type: 'textfield',
									key: 'label',
									value: defaultValues.label,
								},
								{
									label: 'Value',
									type: 'textfield',
									key: 'value',
									value: defaultValues.value,
								},
							]),
				],
				onSubmit,
			});
		},
		Icon: Bars2Icon,
	};
};

export const textareaConfig = ({
	name = 'Text Area',
	dataIndex = 'textArea',
	description = 'A simple textarea',
	onOpenModal,
	defaultValues = { label: undefined, value: undefined },
	onSubmit,
	isEditing,
}) => {
	return {
		name,
		dataIndex,
		description,
		onClick: () => {
			onOpenModal({
				fields: [
					...(isEditing
						? [
								{
									label: defaultValues.label,
									type: 'textarea',
									key: 'value',
									value: defaultValues.value,
								},
							]
						: [
								{
									label: 'Name of field',
									type: 'textfield',
									key: 'label',
									value: defaultValues.label,
								},
								{
									label: 'Value',
									type: 'textarea',
									key: 'value',
									value: defaultValues.value,
								},
							]),
				],
				onSubmit,
			});
		},
		Icon: Bars4Icon,
	};
};

export const datefieldConfig = ({
	name = 'Date Field',
	dataIndex = 'dateField',
	description = 'A simple date field',
	onOpenModal,
	defaultValues = { label: undefined, value: undefined },
	onSubmit,
	isEditing,
}) => {
	return {
		name,
		dataIndex,
		description,
		onClick: () => {
			onOpenModal({
				fields: [
					...(isEditing
						? [
								{
									label: defaultValues.label,
									type: 'datefield',
									key: 'value',
									value: defaultValues.value,
								},
							]
						: [
								{
									label: 'Name of field',
									type: 'textfield',
									key: 'label',
									value: defaultValues.label,
								},
								{
									label: 'Date',
									type: 'datefield',
									key: 'value',
									value: defaultValues.value,
								},
							]),
				],
				onSubmit,
			});
		},
		Icon: CalendarDaysIcon,
	};
};

export const booleanfieldConfig = ({
	name = 'Boolean Field',
	dataIndex = 'booleanField',
	description = 'A simple boolean field',
	onOpenModal,
	defaultValues = { label: undefined, value: undefined },
	onSubmit,
	isEditing,
}) => {
	return {
		name,
		dataIndex,
		description,
		onClick: () => {
			onOpenModal({
				fields: [
					...(isEditing
						? [
								{
									label: defaultValues.label,
									type: 'booleanfield',
									key: 'value',
									value: defaultValues.value,
								},
								{
									label: 'Description',
									type: 'textfield',
									key: 'description',
									value: defaultValues.description,
								},
							]
						: [
								{
									label: 'Name of field',
									type: 'textfield',
									key: 'label',
									value: defaultValues.label,
								},
								{
									label: 'Description',
									type: 'textfield',
									key: 'description',
									value: defaultValues.description,
								},
								{
									label: 'Value',
									type: 'booleanfield',
									key: 'value',
									value: defaultValues.value,
								},
							]),
				],
				onSubmit,
			});
		},
		Icon: ChatBubbleBottomCenterTextIcon,
	};
};
